import React, { useEffect, useState } from "react";
import { Button, CloseButton, Col, Spinner, Table } from "react-bootstrap";
import { useDebounceEffect } from "../../hooks/useDebounceEffect";
import { Search } from "react-bootstrap-icons";
import {
  usePagesByAdminQuery,
  useSetBlockPageMutation,
} from "../../graphql/page.hooks";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/pagination";
import { BlockUserModal } from "../../components/modals/blockUserModal";
import { GraphUser } from "../../../graphql/types";
import { useUserBlockMutation } from "../../graphql/menu.hooks";
import { DeepPartial } from "../../types";

const PageBlocker = () => {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(12);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedUser, setSelectedUser] =
    useState<DeepPartial<GraphUser>>(null);

  const selectUser = (user: DeepPartial<GraphUser>) => setSelectedUser(user);
  const unselectUser = () => setSelectedUser(null);

  const {
    data: pagesData,
    loading: pagesDataLoading,
    refetch: refetchPagesData,
  } = usePagesByAdminQuery({
    variables: { limit: limit, page: currentPage, search: search || "" },
    fetchPolicy: "network-only",
  });

  const [unBlock] = useUserBlockMutation({
    onCompleted: () => {
      refetchPagesData();
    },
  });

  const [setBlockPage, { loading: setBlockPageLoading }] =
    useSetBlockPageMutation({
      onCompleted: () => {
        refetchPagesData({ search: "" });
      },
    });

  useEffect(() => {
    document.title = "Page Blocker";
  }, []);

  const pagination = pagesData?.pagesByAdmin?.paginator;
  const pagesCount = pagination?.totalPages || 0;
  const totalItems = pagination?.totalItemCount || 0;
  const firstItemIndex = pagination
    ? (currentPage - 1) * pagination.itemNumberPerPage + 1
    : 0;
  const lastItemIndex = pagination
    ? Math.min(currentPage * pagination.itemNumberPerPage, totalItems)
    : 0;

  const formateDate = (isoString: string) => {
    const date = new Date(isoString);

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    return `${dd}.${mm}.${yyyy}`;
  };

  const handleBlock = ({
    pageId,
    block,
  }: {
    pageId: number;
    block: boolean;
  }) => {
    setBlockPage({
      variables: {
        pageId,
        block,
      },
    });
  };

  const tryUnBlockUser = async (user: DeepPartial<GraphUser>) => {
    try {
      await unBlock({
        variables: { userId: user?.id, status: false, comment: "" },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    try {
      await refetchPagesData({
        search: search,
        page: currentPage,
        limit,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useDebounceEffect(handleSearch, 500, [search]);

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetchPagesData({
      search: search,
      page: 1,
      limit: newLimit,
    });
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  if (setBlockPageLoading)
    return <Spinner animation="border" role="status"></Spinner>;
  return (
    <>
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="mb-4">
            <h1>Page Blocker</h1>
          </div>
        </div>

        <div className="g-4 pt-5 bg-white rounded">
          <Col xs={12} md={5} className="ms-auto px-5 mb-4">
            <div className="templateSearch d-flex align-items-center px-2 pb-2 me-4 w-100">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="templateInput text-gray me-3 w-100"
              />
              {search.length > 0 && (
                <CloseButton
                  className="text-dark me-2"
                  onClick={() => setSearch("")}
                />
              )}
              <Search className="text-dark" />
            </div>
          </Col>
          <div className="overflow-x-auto px-3 pb-3">
            <Table className="table table-responsive table-borderless">
              <thead>
                <tr>
                  <th className="text-center">Page ID</th>
                  <th className="text-center text-nowrap">Project ID</th>
                  <th>Email</th>
                  <th>CreatedAt</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <>
                  {pagesData?.pagesByAdmin?.pages?.map((page) => (
                    <tr
                      key={page.id}
                      style={{ lineHeight: "2.5", height: "50px" }}
                    >
                      <td style={{ width: "75px" }} className="text-center">
                        {page.id}
                      </td>
                      <td className="text-center">{page?.project?.id}</td>
                      <td style={{ width: "250px" }} className="text-nowrap">
                        {page.user?.email}
                      </td>
                      <td
                        style={{ width: "100px" }}
                        className="text-nowrap text-center"
                      >
                        {page.createdAt ? (
                          formateDate(page.createdAt)
                        ) : (
                          <i className="bi bi-dash-lg text-dark-gray"></i>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="link"
                          className={`${page?.userGraphObject?.userBlock?.status ? "text-danger" : "text-primary"} text-decoration-none`}
                          onClick={() => {
                            const user = page?.userGraphObject;

                            if (!user?.userBlock?.status) {
                              selectUser(user);
                              setShowBlockModal(true);
                            } else {
                              tryUnBlockUser(user);
                            }
                          }}
                        >
                          {page?.userGraphObject?.userBlock?.status
                            ? "Unblock User"
                            : "Block User"}
                        </Button>
                        <Button
                          variant="link"
                          className={`${page.block ? "text-danger" : "text-primary"} text-decoration-none`}
                          onClick={() =>
                            handleBlock({ pageId: page.id, block: !page.block })
                          }
                        >
                          {page.block ? "Unblock" : "Block"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </>
              </tbody>
            </Table>
            {pagesDataLoading && (
              <div className="d-flex align-items-center justify-content-center my-4">
                <Spinner animation="border" role="status" />
              </div>
            )}
            {!pagesData?.pagesByAdmin?.pages?.length &&
              !pagesDataLoading &&
              !setBlockPageLoading && (
                <div className="text-center my-4">No pages found</div>
              )}
          </div>
        </div>
        <div className="col-12 d-flex justify-content-between align-items-center bg-white px-3 px-md-5 py-3 mt-5 rounded">
          <Pagination
            t={t}
            pages={pagesCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            firstItemIndex={firstItemIndex}
            lastItemIndex={lastItemIndex}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        </div>
      </div>
      {showBlockModal && (
        <BlockUserModal
          showModal={showBlockModal}
          refetch={refetchPagesData}
          handleCloseModal={() => {
            unselectUser();
            setShowBlockModal(false);
          }}
          selectedUser={selectedUser}
        />
      )}
    </>
  );
};

export default PageBlocker;
